import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import classNames from 'classnames'
import { X } from 'lucide-react'
import Spinner from '@/components/spinner'
import styles from './style.module.css'

type Size = 'lg' | 'md' | 'sm' | 'xl'

interface DefaultModalProps {
  isOpen: boolean
  toggle: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: Size
  isLoading?: boolean
  closeOnOverlay?: boolean
  className?: string
}

const DefaultModal = ({
  children,
  className,
  closeOnOverlay = true,
  footer,
  isLoading = false,
  isOpen,
  size = 'md',
  title,
  toggle,
}: DefaultModalProps) => {
  const inicioNoOverlay = useRef(false)

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') toggle()
    }

    document.addEventListener('keydown', handleKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, toggle])

  if (!isOpen) return null

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(event) => {
        inicioNoOverlay.current = event.target === event.currentTarget
      }}
      onMouseUp={(event) => {
        const fecharPeloOverlay =
          closeOnOverlay && inicioNoOverlay.current && event.target === event.currentTarget
        inicioNoOverlay.current = false
        if (fecharPeloOverlay) toggle()
      }}
      role="presentation"
    >
      <div
        className={classNames(styles.dialog, styles[size], className)}
        role="dialog"
        aria-modal="true"
      >
        {isLoading ? (
          <div className={styles.loading}>
            <Spinner size={32} />
          </div>
        ) : null}

        <header className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <button type="button" className={styles.close} onClick={toggle} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  )
}

export default DefaultModal
